<script setup lang="ts">
/**
 * A picture.
 *
 * The file is read into the document as a data URL rather than uploaded and
 * linked. That makes the document larger and makes it self-contained, which is
 * the trade worth taking: a report opened next year should not depend on a
 * file server somebody has since tidied up.
 */
import { useI18n } from 'vue-i18n'

import { ref } from 'vue'

import InspectorField from '@/sections/InspectorField.vue'
import type { ImageSection } from '@/domain/section'

const { t } = useI18n()

const props = defineProps<{ section: ImageSection }>()
const emit = defineEmits<{ change: [section: ImageSection] }>()

/** Beyond this, a document becomes slow to save and slow to open. */
const MAX_BYTES = 4 * 1024 * 1024

const problem = ref<string | null>(null)

function set(changes: Partial<ImageSection>): void {
  emit('change', { ...props.section, ...changes })
}

function choose(event: Event): void {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return

  problem.value = null

  if (!file.type.startsWith('image/')) {
    problem.value = t('inspector.image.notAnImage')
    return
  }

  if (file.size > MAX_BYTES) {
    problem.value = t('inspector.image.tooLarge')
    return
  }

  const reader = new FileReader()
  reader.onload = () => {
    if (typeof reader.result === 'string') set({ source: reader.result })
  }
  reader.onerror = () => {
    problem.value = t('inspector.image.unreadable')
  }
  reader.readAsDataURL(file)
}
</script>

<template>
  <div class="fields">
    <InspectorField :label="t('inspector.image.file')">
      <input class="b-input" type="file" accept="image/*" @change="choose" />
    </InspectorField>

    <p v-if="problem" class="problem" role="alert">{{ problem }}</p>

    <InspectorField :label="t('inspector.image.caption')">
      <input
        class="b-input"
        :value="section.caption"
        @input="set({ caption: ($event.target as HTMLInputElement).value })"
      />
    </InspectorField>

    <InspectorField
      :label="t('inspector.image.width')"
      :hint="t('inspector.image.widthHint', { percent: section.width })"
    >
      <input
        class="range"
        type="range"
        min="20"
        max="100"
        step="5"
        :value="section.width"
        @input="set({ width: Number(($event.target as HTMLInputElement).value) })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.range {
  width: 100%;
  accent-color: var(--accent);
}

.problem {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-sm);
}
</style>
